import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css/navigation";
import "swiper/css";

function generateGridLayout(images, containerWidth, containerHeight, containerRef) {
    if (!images || !images.length || !containerWidth) return { layout: [], totalHeight: 0 };

    const imageCount = images.length;
    const gap = 20;

    const layout = [];
    let rows = [];
    let currentRow = [];
    let currentRowWidth = 0;
    let currentY = 0;

    if (imageCount === 1) {
        return {
            layout: [{
                imageIndex: 0,
                src: images[0].src,
                caption: images[0].caption,
                alt: images[0].alt,
                gridX: 0,
                gridY: 0,
                gridWidth: containerWidth,
                gridHeight: (Math.max(containerHeight))
            }],
            totalHeight: Math.max(containerHeight, containerWidth * 0.6)
        };
    } else if (imageCount === 2) {
        if (containerRef?.current?.offsetWidth > 575) {
            const width = (containerWidth - gap) / 2;
            const height = Math.max(containerHeight);

            return {
                layout: [
                    { imageIndex: 0, src: images[0].src, caption: images[0].caption, alt: images[0].alt, gridX: 0, gridY: 0, gridWidth: width, gridHeight: height },
                    { imageIndex: 1, src: images[1].src, caption: images[1].caption, alt: images[1].alt, gridX: width + gap, gridY: 0, gridWidth: width, gridHeight: height }
                ],
                totalHeight: height
            };
        } else {
            // Below 575px, set a single image per row
            const height = ((containerHeight / imageCount)); // Distribute container height equally among the images

            return {
                layout: images.map((image, index) => ({
                    imageIndex: index,
                    src: image.src,
                    caption: image.caption,
                    alt: image.alt,
                    gridX: 0,
                    gridY: index * (height + gap), // Stack the images vertically with the gap between them
                    gridWidth: containerWidth,
                    gridHeight: height
                })),
                totalHeight: height * imageCount + gap * (imageCount - 1) // Total height will be image height * image count + gaps
            };
        }
    }
    else if (imageCount === 3) {
        if (containerRef?.current?.offsetWidth > 575) {
            const mainWidth = containerWidth * 0.61;
            const sideWidth = containerWidth - mainWidth - gap;
            const mainHeight = (Math.max(containerHeight));
            const sideHeight = (mainHeight - gap) / 2;

            return {
                layout: [
                    { imageIndex: 0, src: images[0].src, caption: images[0].caption, alt: images[0].alt, gridX: 0, gridY: 0, gridWidth: mainWidth, gridHeight: mainHeight },
                    { imageIndex: 1, src: images[1].src, caption: images[1].caption, alt: images[1].alt, gridX: mainWidth + gap, gridY: 0, gridWidth: sideWidth, gridHeight: sideHeight },
                    { imageIndex: 2, src: images[2].src, caption: images[2].caption, alt: images[2].alt, gridX: mainWidth + gap, gridY: sideHeight + gap, gridWidth: sideWidth, gridHeight: sideHeight }
                ],
                totalHeight: mainHeight
            };
        } else {
            // Below 575px, set a single image per row
            const height = containerHeight / imageCount; // Distribute container height equally among the images

            return {
                layout: images.map((image, index) => ({
                    imageIndex: index,
                    src: image.src,
                    caption: image.caption,
                    alt: image.alt,
                    gridX: 0,
                    gridY: index * (height + gap), // Stack the images vertically with the gap between them
                    gridWidth: containerWidth,
                    gridHeight: height
                })),
                totalHeight: height * imageCount + gap * (imageCount - 1) // Total height will be image height * image count + gaps
            };
        }
    }
    else if (imageCount === 4) {
        if (containerRef?.current?.offsetWidth > 575) {
            // For larger screens (above 607px), use the original grid layout with 2 columns
            const width = (containerWidth - gap) / 2;
            const height = Math.max((containerHeight - gap) * 0.5);

            return {
                layout: [
                    { imageIndex: 0, src: images[0].src, caption: images[0].caption, alt: images[0].alt, gridX: 0, gridY: 0, gridWidth: width, gridHeight: height },
                    { imageIndex: 1, src: images[1].src, caption: images[1].caption, alt: images[1].alt, gridX: width + gap, gridY: 0, gridWidth: width, gridHeight: height },
                    { imageIndex: 2, src: images[2].src, caption: images[2].caption, alt: images[2].alt, gridX: 0, gridY: height + gap, gridWidth: width, gridHeight: height },
                    { imageIndex: 3, src: images[3].src, caption: images[3].caption, alt: images[3].alt, gridX: width + gap, gridY: height + gap, gridWidth: width, gridHeight: height }
                ],
                totalHeight: height * 2 + gap // Calculate total height for 2 rows
            };
        } else {
            // Below 607px (mobile or small screen): Use a single column layout for 4 images
            const height = containerHeight / imageCount; // Distribute the container height equally among the images

            return {
                layout: images.map((image, index) => ({
                    imageIndex: index,
                    src: image.src,
                    caption: image.caption,
                    alt: image.alt,
                    gridX: 0, // Set X position to 0 for full width
                    gridY: index * (height + gap), // Stack the images vertically with a gap between them
                    gridWidth: containerWidth, // Each image takes up the full width
                    gridHeight: height // Each image gets an equal portion of the container height
                })),
                totalHeight: height * imageCount + gap * (imageCount - 1) // Total height including gaps
            };
        }
    } else if (imageCount === 6) {
        if (containerRef?.current?.offsetWidth > 768) {
            const width = (containerWidth - gap * 2) / 3; // Each image will take up 1/3 of the container width for each row
            const height = Math.max((containerHeight - gap) * 0.5); // Set a minimum height for the images
    
            return {
                layout: [
                    // First row with 3 images
                    { imageIndex: 0, src: images[0].src, caption: images[0].caption, alt: images[0].alt, gridX: 0, gridY: 0, gridWidth: width, gridHeight: height },
                    { imageIndex: 1, src: images[1].src, caption: images[1].caption, alt: images[1].alt, gridX: width + gap, gridY: 0, gridWidth: width, gridHeight: height },
                    { imageIndex: 2, src: images[2].src, caption: images[2].caption, alt: images[2].alt, gridX: (width + gap) * 2, gridY: 0, gridWidth: width, gridHeight: height },
    
                    // Second row with 3 images
                    { imageIndex: 3, src: images[3].src, caption: images[3].caption, alt: images[3].alt, gridX: 0, gridY: height + gap, gridWidth: width, gridHeight: height },
                    { imageIndex: 4, src: images[4].src, caption: images[4].caption, alt: images[4].alt, gridX: width + gap, gridY: height + gap, gridWidth: width, gridHeight: height },
                    { imageIndex: 5, src: images[5].src, caption: images[5].caption, alt: images[5].alt, gridX: (width + gap) * 2, gridY: height + gap, gridWidth: width, gridHeight: height }
                ],
                totalHeight: height * 2 + gap * 3 // Total height considering the two rows and gaps between them
            };
        }
    }
     else if (imageCount === 8 && containerRef?.current?.offsetWidth >  1300 && containerRef?.current?.offsetWidth < 1820) {
        const width = (containerWidth - gap * 3) / 4;   
        const height = Math.max((containerHeight - gap) * 0.5); // Adjust height as needed
    
        return {
            layout: [
                // First row with 4 images
                { imageIndex: 0, src: images[0].src, caption: images[0].caption, alt: images[0].alt, gridX: 0, gridY: 0, gridWidth: width, gridHeight: height },
                { imageIndex: 1, src: images[1].src, caption: images[1].caption, alt: images[1].alt, gridX: width + gap, gridY: 0, gridWidth: width, gridHeight: height },
                { imageIndex: 2, src: images[2].src, caption: images[2].caption, alt: images[2].alt, gridX: (width + gap) * 2, gridY: 0, gridWidth: width, gridHeight: height },
                { imageIndex: 3, src: images[3].src, caption: images[3].caption, alt: images[3].alt, gridX: (width + gap) * 3, gridY: 0, gridWidth: width, gridHeight: height },
    
                // Second row with 4 images
                { imageIndex: 4, src: images[4].src, caption: images[4].caption, alt: images[4].alt, gridX: 0, gridY: height + gap, gridWidth: width, gridHeight: height },
                { imageIndex: 5, src: images[5].src, caption: images[5].caption, alt: images[5].alt, gridX: width + gap, gridY: height + gap, gridWidth: width, gridHeight: height },
                { imageIndex: 6, src: images[6].src, caption: images[6].caption, alt: images[6].alt, gridX: (width + gap) * 2, gridY: height + gap, gridWidth: width, gridHeight: height },
                { imageIndex: 7, src: images[7].src, caption: images[7].caption, alt: images[7].alt, gridX: (width + gap) * 3, gridY: height + gap, gridWidth: width, gridHeight: height }
            ],
            totalHeight: height * 2 + gap * 3 
        };
    } else if (imageCount === 9 && containerRef?.current?.offsetWidth > 1523) {
        const widthFirstRow = (containerWidth - gap * 3) / 4; // First row will have 4 images, each taking up 1/4 of the container width (minus gaps)
        const widthSecondRow = (containerWidth - gap * 4) / 5; // Second row will have 5 images, each taking up 1/5 of the container width (minus gaps)
    
        const heightFirstRow = Math.max((containerHeight - gap) * (1 / 2)); // First row height as 1/3 of container height
        const heightSecondRow = Math.max((containerHeight - gap)* (1 / 2)); // Second row height as 1/2 of container height
    
        return {
            layout: [
                // First row with 4 images
                { imageIndex: 0, src: images[0].src, caption: images[0].caption, alt: images[0].alt, gridX: 0, gridY: 0, gridWidth: widthFirstRow, gridHeight: heightFirstRow },
                { imageIndex: 1, src: images[1].src, caption: images[1].caption, alt: images[1].alt, gridX: widthFirstRow + gap, gridY: 0, gridWidth: widthFirstRow, gridHeight: heightFirstRow },
                { imageIndex: 2, src: images[2].src, caption: images[2].caption, alt: images[2].alt, gridX: (widthFirstRow + gap) * 2, gridY: 0, gridWidth: widthFirstRow, gridHeight: heightFirstRow },
                { imageIndex: 3, src: images[3].src, caption: images[3].caption, alt: images[3].alt, gridX: (widthFirstRow + gap) * 3, gridY: 0, gridWidth: widthFirstRow, gridHeight: heightFirstRow },
    
                // Second row with 5 images
                { imageIndex: 4, src: images[4].src, caption: images[4].caption, alt: images[4].alt, gridX: 0, gridY: heightFirstRow + gap, gridWidth: widthSecondRow, gridHeight: heightSecondRow },
                { imageIndex: 5, src: images[5].src, caption: images[5].caption, alt: images[5].alt, gridX: widthSecondRow + gap, gridY: heightFirstRow + gap, gridWidth: widthSecondRow, gridHeight: heightSecondRow },
                { imageIndex: 6, src: images[6].src, caption: images[6].caption, alt: images[6].alt, gridX: (widthSecondRow + gap) * 2, gridY: heightFirstRow + gap, gridWidth: widthSecondRow, gridHeight: heightSecondRow },
                { imageIndex: 7, src: images[7].src, caption: images[7].caption, alt: images[7].alt, gridX: (widthSecondRow + gap) * 3, gridY: heightFirstRow + gap, gridWidth: widthSecondRow, gridHeight: heightSecondRow },
                { imageIndex: 8, src: images[8].src, caption: images[8].caption, alt: images[8].alt, gridX: (widthSecondRow + gap) * 4, gridY: heightFirstRow + gap, gridWidth: widthSecondRow, gridHeight: heightSecondRow }
            ],
            totalHeight: heightFirstRow + heightSecondRow + gap * 3 // Total height for both rows plus gaps
        };
    }
    
    
    
    


    for (let i = 0; i < imageCount; i++) {
        let aspectRatio;
        if (i === 0) {
            aspectRatio = 1.5;
        } else if (i % 6 === 3) {
            aspectRatio = 1.6;
        } else if (i % 9 === 7) {
            aspectRatio = 0.8;
        } else {
            aspectRatio = 1 + (Math.random() * 0.4 - 0.2);
        }

        currentRow.push({
            imageIndex: i,
            aspectRatio
        });

        const targetHeight = 350;
        const virtualWidth = targetHeight * aspectRatio;
        currentRowWidth += virtualWidth;

        if (currentRowWidth + (gap * (currentRow.length - 1)) >= containerWidth * 0.9 || i === imageCount - 1) {
            rows.push({
                images: [...currentRow],
                virtualWidth: currentRowWidth
            });
            currentRow = [];
            currentRowWidth = 0;
        }
    }

    rows.forEach(row => {
        const rowImages = row.images;

        const rowWidth = containerWidth;
        const totalGapWidth = gap * (rowImages.length - 1);
        const availableWidth = rowWidth - totalGapWidth;

        const scaleFactor = availableWidth / row.virtualWidth;

        let currentX = 0;

        rowImages.forEach((image, index) => {
            let targetHeight = 350;
            let virtualWidth = targetHeight * image.aspectRatio;

            let width = virtualWidth * scaleFactor;
            let height = targetHeight * scaleFactor;
            if (index === rowImages.length - 1) {
                width = width;
            }

            layout.push({
                imageIndex: image.imageIndex,
                src: images[image.imageIndex].src,
                caption: images[image.imageIndex].caption,
                alt: images[image.imageIndex].alt,
                gridX: currentX,
                gridY: currentY,
                gridWidth: width,
                gridHeight: height
            });

            currentX += width + gap;
        });

        currentY += rowImages.length > 0 ? layout[layout.length - 1].gridHeight + gap : 0;
    });

    const totalHeight = Math.max(currentY, containerHeight);

    return { layout, totalHeight };
}


const GalleryImages = ({images, theme }) => {
    const containerRef = useRef(null);
    const prevRef = useRef(null);
    const nextRef = useRef(null);
    const swiperRef = useRef(null);
    const popupRef = useRef(null); 
    const [imgs, setImgs] = useState(images);

    const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
    const [imgsLoaded, setImgsLoaded] = useState(false);
    const [isSliderOpen, setIsSliderOpen] = useState(false); 
    const [currentIndex, setCurrentIndex] = useState(0);
    const [swiperInstance, setSwiperInstance] = useState(null);

    const handleClickImage = (index) => {
        setCurrentIndex(index);
        setIsSliderOpen(true);
    };

    useLayoutEffect(() => {
        const handleResize = () => {
            if (containerRef.current) {
                setDimensions({
                    width: containerRef.current.offsetWidth,
                    height: containerRef.current.offsetHeight
                });
            }
        };

        window.addEventListener('resize', handleResize);
        handleResize();

        return () => window.removeEventListener('resize', handleResize);
    }, [imgsLoaded, containerRef?.current?.offsetWidth, containerRef?.current?.offsetHeight]);
    useEffect(() => {
        if (swiperInstance) {
            if (swiperRef?.current && swiperRef?.current?.swiper) {
                swiperRef.current.swiper.params.navigation.prevEl = prevRef?.current;
                swiperRef.current.swiper.params.navigation.nextEl = nextRef?.current;
                swiperRef?.current?.swiper?.navigation.init();
                swiperRef?.current?.swiper?.navigation.update();
            }
        }
    }, [prevRef?.current, nextRef?.current, swiperInstance]);

    useEffect(() => {
        if (imgs.length > 0 && dimensions.width > 0 && dimensions.height > 0) {

            const optimizedImages = generateGridLayout(imgs, dimensions.width, dimensions.height, containerRef);
            setImgs(optimizedImages?.layout || imgs);
            setImgsLoaded(true);
        }
    }, [dimensions, imgs.length]); useEffect(() => {
        const handleOutsideClick = (event) => {
            if (popupRef.current && !popupRef.current.contains(event.target)) {
                setIsSliderOpen(false);
            }
        };
        document.addEventListener('mousedown', handleOutsideClick);
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, []);

    return (
        <>
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" className={`closePopUp`} onClick={() => setIsSliderOpen(false)}>
                <path d="M11 1L1 11M1 1L11 11" stroke={`${theme === 'dark' ? '#E1E1E1' : '#667085' }`} strokeWidth="1.66667" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div ref={containerRef} className={`galleryContainer`}>
                <div className={`imageGallery`}>
                    {Object.values(imgs).map((img, index) => (
                        <img
                            src={img.src}
                            alt={img.alt}
                            width={img?.gridWidth || 0}
                            height={img?.gridHeight || 0}
                            key={index}
                            style={{
                                position: "absolute",
                                left: `${img?.gridX || 0}px`,
                                top: `${img?.gridY || 0}px`,
                                width: `${img?.gridWidth || 0}px`,
                                height: `${img?.gridHeight || 0}px`,
                                objectFit: 'cover',
                                cursor: 'pointer'
                            }}
                            onClick={() => handleClickImage(index)} // Click handler to open the slider
                        />
                    ))}
                </div>

                {/* Swiper Slider */}
                {isSliderOpen && (
                    <div>
                        <div className={`sliderOverlay`}></div>

                        <div className={`sliderPopup`} ref={popupRef}>
                            <div>
                                <Swiper
                                    initialSlide={currentIndex}
                                    slidesPerView={1}
                                    pagination={{ clickable: true }}
                                    loop
                                    onSwiper={setSwiperInstance}
                                    modules={[Pagination, Navigation]}
                                    navigation={{
                                        prevEl: prevRef.current,
                                        nextEl: nextRef.current,
                                    }}
                                    ref={swiperRef}
                                    className={`swiper`}
                                >
                                    {imgs?.map((img, index) => {
                                        console.log(img)
                                        return <>
                                            <SwiperSlide key={index}>
                                                <img
                                                    src={img?.src}
                                                    alt={img?.alt}
                                                    key={index}
                                                    className={`sliderImage`}
                                                />
                                                {img?.caption !== '' &&
                                                    <div className={`imageCaptionDiv`}>
                                                        <div className={`cornerBottomLeft bottom`}>
                                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
                                                            </svg>
                                                        </div>
                                                        <div className={`imageCaptionSection`}>
                                                            <div className={`imageCaption`}>{img?.caption}</div>
                                                        </div>
                                                        <div className={`cornerBottomRight bottom`}>
                                                            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                                <path d="M0 0L0 20C0 8.95431 8.95431 0 20 0L0 0Z" fill="white"></path>
                                                            </svg>
                                                        </div>
                                                    </div>
                                                }
                                            </SwiperSlide>
                                        </>
                                    })}
                                </Swiper>
                                <div className={`swiperButton`}>
                                    <button className={`prev`} ref={prevRef}>
                                        <svg width="14" height="13" viewBox="0 0 14 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M8.07766 11.9453L7.4761 12.5469C7.20266 12.793 6.7925 12.793 6.54641 12.5469L1.24172 7.21484C0.968286 6.96875 0.968286 6.55859 1.24172 6.3125L6.54641 0.980469C6.7925 0.734375 7.23 0.734375 7.4761 0.980469L8.07766 1.58203C8.3511 1.85547 8.32375 2.26562 8.07766 2.53906L4.76907 5.65625H12.6441C12.9995 5.65625 13.3003 5.95703 13.3003 6.3125V7.1875C13.3003 7.57031 12.9995 7.84375 12.6441 7.84375H4.76907L8.07766 10.9883C8.32375 11.2617 8.3511 11.6719 8.07766 11.9453Z" />
                                        </svg>
                                    </button>
                                    <button className={`next`} ref={nextRef}>
                                        <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M5.24563 1.58203L5.84719 0.980469C6.12063 0.734375 6.53079 0.734375 6.77688 0.980469L12.1089 6.28516C12.355 6.55859 12.355 6.96875 12.1089 7.21484L6.77688 12.5469C6.53079 12.793 6.12063 12.793 5.84719 12.5469L5.24563 11.9453C4.99954 11.6719 4.99954 11.2617 5.24563 10.9883L8.55422 7.84375H0.706567C0.323755 7.84375 0.0503173 7.57031 0.0503173 7.1875V6.3125C0.0503173 5.95703 0.323755 5.65625 0.706567 5.65625H8.55422L5.24563 2.53906C4.99954 2.26562 4.97219 1.85547 5.24563 1.58203Z" />
                                        </svg>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </>
    )
}

export default GalleryImages